import { Button } from '@/components/Button'
import { Input } from '@/components/form/Input'
import { useForm } from 'react-hook-form'
import { IoAdd, IoImage, IoOpenOutline, IoTrash } from 'react-icons/io5'
import { DragEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArtistSocialLinkProps,
  ArtistsEditDataProps,
  ArtistsResponseProps,
} from '@/types/artistsProps'
import {
  CoverAssetStatus,
  DiscoveryStatus,
  ProfileClaimStatus,
  VisualAssetStatus,
  coverAssetStatusOptions,
  profileClaimStatusOptions,
  visualAssetStatusOptions,
} from '@/types/catalogGovernance'
import { useToast } from '@/hooks/useToast'
import { useModal } from '@/hooks/useModal'
import { useDispatch, useSelector } from 'react-redux'
import { useKeenSlider } from 'keen-slider/react'
import { handleSetArtists } from '@/storage/modules/artists/reducer'
import { MusicResponseProps } from '@/types/musicProps'
import Skeleton from 'react-loading-skeleton'
import { FormMusic } from '../FormMusic'
import { ReduxProps } from '@/storage'
import { SocialMediaProps } from '@/storage/modules/socialMedia/reducer'
import { MusicalGenresDataProps } from '@/types/musicalGenresProps'
import colors from 'tailwindcss/colors'
import { FileWithType, useUpload } from '@/hooks/useUpload'
import { api } from '@/services/api'
import { uploadArtistAsset } from '@/services/upload-artist-photos'
import { AxiosError } from 'axios'
import { CatalogStatusBadge } from '../CatalogStatusBadge'
import { fetchGenresOptions } from '@/services/genres'
import { notifyArtistsUpdated } from '@/services/artistsEvents'

interface FormArtistProps {
  artist?: ArtistsResponseProps
}

const textInputClassName =
  'w-full bg-white border border-gray-300 rounded px-4 py-2 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 text-sm font-normal'

const infoCardClassName = 'rounded-2xl border border-gray-200 bg-gray-50/60 p-4'

const createEmptySocialLink = (): ArtistSocialLinkProps => ({
  socialMediaId: '',
  url: '',
})

const normalizeOptionalText = (value?: string | null) => {
  const normalizedValue = value?.trim()

  return normalizedValue || null
}

export function FormArtist({ artist }: FormArtistProps) {
  const [artistDetails, setArtistDetails] = useState<ArtistsResponseProps>()
  const [isLoading, setIsLoading] = useState(false)
  const [isPulisherLoading, setIsPublisherLoading] = useState(false)
  const [governanceActionLoading, setGovernanceActionLoading] = useState<
    'profileClaim' | 'visualAsset' | 'coverAsset' | null
  >(null)
  const [musics, setMusics] = useState<MusicResponseProps[]>()
  const [isDrop, setIsDrop] = useState(false)
  const [isDropCover, setIsDropCover] = useState(false)
  const [isUpdatePhoto, setIsUpdatePhoto] = useState(false)
  const [isUpdateCover, setIsUpdateCover] = useState(false)
  const [discoveryStatus, setDiscoveryStatus] =
    useState<DiscoveryStatus>('DRAFT')
  const [profileClaimStatus, setProfileClaimStatus] =
    useState<ProfileClaimStatus>('UNCLAIMED')
  const [visualAssetStatus, setVisualAssetStatus] =
    useState<VisualAssetStatus>('NONE')
  const [coverAssetStatus, setCoverAssetStatus] =
    useState<CoverAssetStatus>('NONE')
  const [socialLinks, setSocialLinks] = useState<ArtistSocialLinkProps[]>([])
  const [musicalGenresSelected, setMusicalGenresSelected] = useState<
    MusicalGenresDataProps[]
  >([])
  const [genreSearchQuery, setGenreSearchQuery] = useState('')
  const [genreOptions, setGenreOptions] = useState<MusicalGenresDataProps[]>([])
  const [isLoadingGenreOptions, setIsLoadingGenreOptions] = useState(false)
  const [isGenreDropdownOpen, setIsGenreDropdownOpen] = useState(false)
  const [file, setFile] = useState<FileWithType>()
  const [coverFile, setCoverFile] = useState<FileWithType>()
  const genreSearchRequestIdRef = useRef(0)

  const { register, handleSubmit, setValue } = useForm<ArtistsEditDataProps>()
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const { closeModal, openModal } = useModal()
  const { formatBytes, formatDate } = useUpload()

  const [sliderRef] = useKeenSlider({
    slides: {
      perView: 5.25,
      spacing: 12,
    },
    breakpoints: {
      '(max-width: 1250px)': {
        slides: {
          perView: 3.2,
          spacing: 12,
        },
      },
      '(max-width: 899px)': {
        slides: {
          perView: 1.75,
          spacing: 12,
        },
      },
    },
  })

  const { socialMedia } = useSelector<ReduxProps, SocialMediaProps>(
    (state) => state.socialMedia,
  )

  const currentArtist = artistDetails ?? artist

  const normalizedGenreSearchQuery = genreSearchQuery.trim()

  const genreSuggestions = useMemo(() => {
    if (normalizedGenreSearchQuery.length < 1) {
      return []
    }

    const normalizedQuery = normalizedGenreSearchQuery.toLowerCase()
    const selectedGenresMap = new Set(
      musicalGenresSelected.map((genre) => genre.id),
    )

    return genreOptions.filter(
      (genre) =>
        !selectedGenresMap.has(genre.id) &&
        genre.title.toLowerCase().includes(normalizedQuery),
    )
  }, [genreOptions, musicalGenresSelected, normalizedGenreSearchQuery])

  const socialMediaOptions = useMemo(() => {
    const options = socialMedia?.map((item) => ({
      label: item.title,
      value: item.id,
    }))

    if (!options) return

    return [{ label: 'Select', value: '' }, ...options]
  }, [socialMedia])

  const reloadArtists = async () => {
    const responseArtists = await api
      .get('/artists')
      .then((res) => res.data.data as ArtistsResponseProps[])

    dispatch(handleSetArtists({ artists: responseArtists }))
  }

  const loadArtistDetails = async (artistId: string) => {
    const responseArtist = await api
      .get(`/artists/${artistId}`)
      .then((res) => res.data as ArtistsResponseProps)

    setArtistDetails(responseArtist)

    return responseArtist
  }

  const syncArtistData = async (artistId?: string) => {
    await reloadArtists()

    if (artistId) {
      await loadArtistDetails(artistId)
    }
  }

  const handleGetMusics = async (id: string) => {
    try {
      const responseMusics = await api.get(`/musics?artistId=${id}`)

      setMusics(responseMusics.data.data as MusicResponseProps[])
    } catch (error) {
      console.log(error)
    }
  }

  const handleSelectMusicalGenre = (genre: MusicalGenresDataProps) => {
    const isExists = musicalGenresSelected.find((item) => item.id === genre.id)

    if (isExists) return

    setMusicalGenresSelected((prev) => [...prev, genre])
    setGenreSearchQuery('')
    setIsGenreDropdownOpen(false)
    setValue('musicalGenres', '')
  }

  const handleSocialLinkChange = (
    index: number,
    field: 'socialMediaId' | 'url',
    value: string,
  ) => {
    setSocialLinks((prev) =>
      prev.map((link, currentIndex) =>
        currentIndex === index ? { ...link, [field]: value } : link,
      ),
    )
  }

  const handleAddSocialLink = () => {
    setSocialLinks((prev) => [...prev, createEmptySocialLink()])
  }

  const handleRemoveSocialLink = (index: number) => {
    setSocialLinks((prev) =>
      prev.filter((_, currentIndex) => currentIndex !== index),
    )
  }

  const normalizeSocialLinks = () =>
    socialLinks
      .map((link) => ({
        socialMediaId: link.socialMediaId,
        url: link.url.trim(),
      }))
      .filter((link) => link.socialMediaId && link.url)

  const hasInvalidSocialLinks = () =>
    socialLinks.some((link) => {
      const hasAnyValue = Boolean(link.socialMediaId || link.url.trim())
      const isComplete = Boolean(link.socialMediaId && link.url.trim())

      return hasAnyValue && !isComplete
    })

  const hasDuplicateSocialMedia = () => {
    const selectedIds = socialLinks
      .map((link) => link.socialMediaId)
      .filter(Boolean)

    return new Set(selectedIds).size !== selectedIds.length
  }

  const uploadArtistFiles = async (title: string) => {
    const [photoUpload, coverUpload] = await Promise.all([
      file ? uploadArtistAsset({ file, title }) : Promise.resolve(null),
      coverFile
        ? uploadArtistAsset({ file: coverFile, title: `${title}-cover` })
        : Promise.resolve(null),
    ])

    const assetPayload: Record<string, string | null> = {}

    if (photoUpload?.privateFile.url) {
      assetPayload.photoURL = photoUpload.privateFile.url
    }

    if (photoUpload?.publicFile.url) {
      assetPayload.publicPhotoURL = photoUpload.publicFile.url
    }

    if (coverUpload?.privateFile.url) {
      assetPayload.coverURL = coverUpload.privateFile.url
    }

    if (coverUpload?.publicFile.url) {
      assetPayload.publicCoverURL = coverUpload.publicFile.url
    }

    return assetPayload
  }

  const handleUpdateArtist = async (data: ArtistsEditDataProps) => {
    if (!currentArtist?.id) return

    setIsLoading(true)

    try {
      await api.patch(`/artists/${currentArtist.id}`, {
        title: data.title,
        bio: normalizeOptionalText(data.bio),
        genreIds: musicalGenresSelected.map((genre) => genre.id),
        socialLinks: normalizeSocialLinks(),
      })

      const assetPayload = await uploadArtistFiles(data.title)

      if (Object.keys(assetPayload).length > 0) {
        await api.patch(`/artists/${currentArtist.id}`, assetPayload)
      }

      await syncArtistData(currentArtist.id)
      notifyArtistsUpdated()

      showToast('Artist updated successfully', {
        type: 'success',
        theme: 'light',
      })

      closeModal()
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message = error.response?.data?.message || 'Error updating artist'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveArtist = async (data: ArtistsEditDataProps) => {
    setIsLoading(true)

    try {
      const responseArtist = await api
        .post('/artists', {
          title: data.title,
          bio: normalizeOptionalText(data.bio),
          photoURL: 'https://i.ibb.co/hZ7QNB3/sonoriza.png',
          genreIds: musicalGenresSelected.map((genre) => genre.id),
          socialLinks: normalizeSocialLinks(),
        })
        .then((response) => response.data as ArtistsResponseProps)

      const assetPayload = await uploadArtistFiles(data.title)

      if (Object.keys(assetPayload).length > 0) {
        await api.patch(`/artists/${responseArtist.id}`, assetPayload)
      }

      await syncArtistData(responseArtist.id)
      notifyArtistsUpdated()

      showToast('Artist added successfully', {
        type: 'success',
        theme: 'light',
      })

      closeModal()
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message = error.response?.data?.message || 'Error creating artist'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handlePublisherArtist = async (artistId: string) => {
    setIsPublisherLoading(true)

    try {
      let operationType = ''

      if (discoveryStatus === 'PUBLISHED') {
        await api.patch(`/artists/${artistId}/unpublish`)
        operationType = 'unpublished'
        setDiscoveryStatus('HIDDEN')
      } else {
        await api.patch(`/artists/${artistId}/publish`)
        operationType = 'published'
        setDiscoveryStatus('PUBLISHED')
      }

      await syncArtistData(artistId)
      notifyArtistsUpdated()

      showToast(`Artist ${operationType} successfully`, {
        type: 'success',
        theme: 'light',
      })
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message =
        error.response?.data?.message || 'Error updating artist publication'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsPublisherLoading(false)
    }
  }

  const handleUpdateProfileClaimStatus = async () => {
    if (!currentArtist?.id) return

    setGovernanceActionLoading('profileClaim')

    try {
      await api.patch(`/artists/${currentArtist.id}/profile-claim-status`, {
        profileClaimStatus,
      })

      await syncArtistData(currentArtist.id)
      notifyArtistsUpdated()

      showToast('Artist profile claim status updated successfully', {
        type: 'success',
        theme: 'light',
      })
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message =
        error.response?.data?.message ||
        'Error updating artist profile claim status'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setGovernanceActionLoading(null)
    }
  }

  const handleUpdateVisualAssetStatus = async () => {
    if (!currentArtist?.id) return

    setGovernanceActionLoading('visualAsset')

    try {
      await api.patch(`/artists/${currentArtist.id}/visual-asset-status`, {
        visualAssetStatus,
      })

      await syncArtistData(currentArtist.id)
      notifyArtistsUpdated()

      showToast('Artist visual asset status updated successfully', {
        type: 'success',
        theme: 'light',
      })
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message =
        error.response?.data?.message ||
        'Error updating artist visual asset status'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setGovernanceActionLoading(null)
    }
  }

  const handleUpdateCoverAssetStatus = async () => {
    if (!currentArtist?.id) return

    setGovernanceActionLoading('coverAsset')

    try {
      await api.patch(`/artists/${currentArtist.id}/cover-asset-status`, {
        coverAssetStatus,
      })

      await syncArtistData(currentArtist.id)
      notifyArtistsUpdated()

      showToast('Artist cover asset status updated successfully', {
        type: 'success',
        theme: 'light',
      })
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message =
        error.response?.data?.message ||
        'Error updating artist cover asset status'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setGovernanceActionLoading(null)
    }
  }

  const handleDropImage = (
    e: DragEvent<HTMLDivElement>,
    target: 'photo' | 'cover',
  ) => {
    e.preventDefault()

    const droppedFile = e.dataTransfer.files[0]
    const imageTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp']

    if (!imageTypes.includes(droppedFile.type)) {
      showToast('Archive type no suported', {
        type: 'error',
        theme: 'light',
      })

      setIsDrop(false)
      setIsDropCover(false)
      return
    }

    if (target === 'photo') {
      setFile(droppedFile)
      setIsDrop(false)
      return
    }

    setCoverFile(droppedFile)
    setIsDropCover(false)
  }

  async function submit(data: ArtistsEditDataProps) {
    if (musicalGenresSelected.length === 0) {
      showToast('Select at least one musical genre', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    if (!currentArtist?.photoURL && !file) {
      showToast('Add artist photo', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    if (hasInvalidSocialLinks()) {
      showToast('Complete or remove incomplete social links', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    if (hasDuplicateSocialMedia()) {
      showToast('Select each social media only once', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    const formArtist: ArtistsEditDataProps = {
      ...data,
      bio: normalizeOptionalText(data.bio),
      publicPhotoURL: currentArtist?.publicPhotoURL ?? null,
      coverURL: currentArtist?.coverURL ?? null,
      publicCoverURL: currentArtist?.publicCoverURL ?? null,
    }

    if (data.id) {
      await handleUpdateArtist(formArtist)
      return
    }

    await handleSaveArtist(formArtist)
  }

  useEffect(() => {
    if (artist?.id) {
      loadArtistDetails(artist.id)
      handleGetMusics(artist.id)
      return
    }

    setArtistDetails(undefined)
    setMusics(undefined)
  }, [artist?.id])

  useEffect(() => {
    if (!currentArtist) return

    setValue('id', currentArtist.id)
    setValue('title', currentArtist.title)
    setValue('photoURL', currentArtist.photoURL ?? '')
    setValue('publicPhotoURL', currentArtist.publicPhotoURL)
    setValue('coverURL', currentArtist.coverURL ?? null)
    setValue('publicCoverURL', currentArtist.publicCoverURL ?? null)
    setValue('bio', currentArtist.bio ?? '')

    if (currentArtist.musicalGenres[0]?.id) {
      setValue('musicalGenres', currentArtist.musicalGenres[0].id)
    }

    setMusicalGenresSelected(currentArtist.musicalGenres)
    setDiscoveryStatus(currentArtist.discoveryStatus)
    setProfileClaimStatus(currentArtist.profileClaimStatus)
    setVisualAssetStatus(currentArtist.visualAssetStatus)
    setCoverAssetStatus(currentArtist.coverAssetStatus)
    setSocialLinks(currentArtist.socialLinks ?? [])
  }, [currentArtist, setValue])

  useEffect(() => {
    let isMounted = true
    const requestId = genreSearchRequestIdRef.current + 1
    genreSearchRequestIdRef.current = requestId

    const loadGenres = async () => {
      try {
        setIsLoadingGenreOptions(true)
        const genresResponse = await fetchGenresOptions()

        if (!isMounted || genreSearchRequestIdRef.current !== requestId) return

        setGenreOptions(genresResponse)
      } catch (error) {
        if (!isMounted || genreSearchRequestIdRef.current !== requestId) return

        showToast('Error fetching genres', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        if (isMounted && genreSearchRequestIdRef.current === requestId) {
          setIsLoadingGenreOptions(false)
        }
      }
    }

    loadGenres()

    return () => {
      isMounted = false
    }
  }, [showToast])

  return (
    <div className="w-full max-w-[920px]">
      <h1 className="font-bold text-xl text-purple-600">
        {currentArtist ? 'Edit' : 'Add new'} artist
      </h1>
      <div className="h-[1px] bg-gray-300/50 my-5" />

      {(currentArtist?.photoURL || currentArtist?.coverURL) && (
        <div className="flex flex-wrap items-start gap-4">
          {currentArtist?.photoURL && (
            <div
              className={`${infoCardClassName} w-full max-w-[180px] md:max-w-none`}
            >
              <p className="font-bold">Artist Photo</p>
              <div className="relative mt-2 w-fit overflow-hidden">
                <button
                  type="button"
                  className="absolute right-0 rounded-full bg-purple-600 p-1 text-white hover:bg-purple-700"
                  onClick={() => setIsUpdatePhoto(!isUpdatePhoto)}
                >
                  <IoOpenOutline />
                </button>
                <img
                  src={currentArtist.photoURL}
                  alt={currentArtist.photoURL}
                  className="h-[72px] w-[72px] rounded-full object-cover"
                />
              </div>
            </div>
          )}

          {currentArtist?.coverURL && (
            <div
              className={`${infoCardClassName} min-w-[280px] flex-1 base:min-w-0`}
            >
              <p className="font-bold">Artist Cover</p>
              <div className="relative mt-2 w-full overflow-hidden">
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded-full bg-purple-600 p-1 text-white hover:bg-purple-700"
                  onClick={() => setIsUpdateCover(!isUpdateCover)}
                >
                  <IoOpenOutline />
                </button>
                <img
                  src={currentArtist.coverURL}
                  alt={currentArtist.coverURL}
                  className="h-32 w-full rounded-2xl object-cover md:h-28"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {currentArtist?.musics && currentArtist.musics.length > 0 && (
        <div className={`mt-4 ${infoCardClassName}`}>
          <p className="font-bold">Musics</p>
          <div>
            {musics ? (
              <div
                ref={sliderRef}
                className="ken-slider mt-3 flex overflow-hidden"
              >
                {musics.map((music) => (
                  <button
                    key={music.id}
                    type="button"
                    className="keen-slider__slide !max-w-[112px] flex flex-col items-start gap-2"
                    onClick={() => {
                      openModal({
                        children: <FormMusic musicId={music.id} />,
                      })
                    }}
                  >
                    <img
                      src={music.artwork}
                      alt="artwork"
                      className="h-[88px] w-[88px] rounded-xl object-cover"
                    />
                    <p className="w-[88px] break-words text-center text-sm font-medium leading-tight">
                      {music.title}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-3 flex gap-4">
                <div className="flex flex-col items-center gap-2">
                  <Skeleton width={112} height={112} borderRadius={12} />
                  <Skeleton height={16} width={112} />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Skeleton width={112} height={112} borderRadius={12} />
                  <Skeleton height={16} width={112} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {musicalGenresSelected.length > 0 && (
        <div className={`mt-4 ${infoCardClassName}`}>
          <p className="font-bold">Musical Genres</p>
          <div className="mt-3 grid grid-cols-3 gap-2 base:grid-cols-2 md:grid-cols-1">
            {musicalGenresSelected.map((genre) => (
              <div
                key={genre.title}
                className="relative flex min-h-[46px] items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-white p-2"
              >
                <button
                  type="button"
                  className="absolute right-0 top-0 bg-red-600 p-1 rounded-bl-md hover:bg-red-700"
                  onClick={() => {
                    setMusicalGenresSelected((prev) =>
                      prev.filter((item) => item.title !== genre.title),
                    )
                  }}
                >
                  <IoTrash color={colors.white} size={12} />
                </button>
                <p className="font-semibold text-center">{genre.title}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(submit)} className="mt-4 flex flex-col">
        <div
          className={`grid gap-4 ${
            currentArtist?.id
              ? 'grid-cols-3 base:grid-cols-2 md:grid-cols-1'
              : 'grid-cols-2 base:grid-cols-1'
          }`}
        >
          <Input
            placeholder="Artist name"
            label="Artist"
            name="title"
            register={register}
            required
          />
          {currentArtist?.id && (
            <Input
              placeholder="ID"
              label="ID"
              name="id"
              register={register}
              required
              disabled
            />
          )}
          <div className="relative">
            <h4 className="font-bold text-sm my-3">Musical Genre</h4>
            <input type="hidden" {...register('musicalGenres')} />
            <input
              value={genreSearchQuery}
              onChange={(event) => {
                setGenreSearchQuery(event.currentTarget.value)
                setIsGenreDropdownOpen(true)
              }}
              onFocus={() => setIsGenreDropdownOpen(true)}
              onBlur={() => {
                window.setTimeout(() => {
                  setIsGenreDropdownOpen(false)
                }, 120)
              }}
              placeholder="Type genre name"
              autoComplete="off"
              className="block w-full rounded border border-gray-300 px-4 py-2 leading-tight focus:border-gray-500 focus:bg-white focus:outline-none"
            />

            {isGenreDropdownOpen && (
              <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                {normalizedGenreSearchQuery.length < 1 && (
                  <p className="px-3 py-2 text-sm text-gray-500">
                    Type at least 1 letter to search.
                  </p>
                )}

                {isLoadingGenreOptions &&
                  normalizedGenreSearchQuery.length >= 1 && (
                    <p className="px-3 py-2 text-sm text-gray-500">
                      Loading...
                    </p>
                  )}

                {!isLoadingGenreOptions &&
                  normalizedGenreSearchQuery.length >= 1 &&
                  genreSuggestions.length === 0 && (
                    <p className="px-3 py-2 text-sm text-gray-500">
                      No genres found.
                    </p>
                  )}

                {!isLoadingGenreOptions &&
                  genreSuggestions.map((genre) => (
                    <button
                      key={genre.id}
                      type="button"
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-purple-50"
                      onMouseDown={() => handleSelectMusicalGenre(genre)}
                    >
                      {genre.title}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-3">
          <h4 className="my-2 text-sm font-bold">Bio</h4>
          <textarea
            {...register('bio')}
            maxLength={1000}
            rows={4}
            placeholder="Artist bio for the public profile"
            className={textInputClassName}
          />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 base:grid-cols-1">
          {(!currentArtist?.id || isUpdatePhoto) && (
            <div className="flex flex-col">
              <h4 className="my-2 text-sm font-bold">New photo</h4>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={() => setIsDrop(true)}
                className="relative flex min-h-[96px] h-auto items-center justify-center overflow-hidden rounded-xl border border-dashed border-purple-600 px-4 py-4"
              >
                {file ? (
                  <div className="flex w-full min-w-0 items-center gap-3">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="h-12 w-12 shrink-0 rounded-full object-cover"
                    />

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {file.name}
                      </p>
                      <span className="text-[#71839B] text-xs font-normal">
                        {formatBytes(file.size)} -{' '}
                        {formatDate(file.lastModified)}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setFile(undefined)}
                      className="ml-auto shrink-0"
                    >
                      <IoTrash color={colors.red[600]} size={20} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-center gap-2 text-center">
                    <IoImage color={colors.purple[600]} size={20} />
                    <p className="font-semibold text-purple-600">
                      Drop an artist photo
                    </p>
                  </div>
                )}

                {isDrop && (
                  <div
                    onDrop={(e) => handleDropImage(e, 'photo')}
                    onDragLeave={() => setIsDrop(false)}
                    className="absolute top-0 flex h-full w-full items-center justify-center bg-black/80"
                  >
                    <div className="pointer-events-none">
                      <p className="text-white font-bold text-xl">
                        Drop your file
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {(!currentArtist?.id ||
            isUpdateCover ||
            !currentArtist?.coverURL) && (
            <div className="flex flex-col">
              <h4 className="my-2 text-sm font-bold">Artist cover</h4>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={() => setIsDropCover(true)}
                className="relative flex min-h-[96px] h-auto items-center justify-center overflow-hidden rounded-xl border border-dashed border-purple-600 px-4 py-4"
              >
                {coverFile ? (
                  <div className="flex w-full min-w-0 items-center gap-3">
                    <img
                      src={URL.createObjectURL(coverFile)}
                      alt={coverFile.name}
                      className="h-12 w-16 shrink-0 rounded-lg object-cover"
                    />

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {coverFile.name}
                      </p>
                      <span className="text-[#71839B] text-xs font-normal">
                        {formatBytes(coverFile.size)} -{' '}
                        {formatDate(coverFile.lastModified)}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setCoverFile(undefined)}
                      className="ml-auto shrink-0"
                    >
                      <IoTrash color={colors.red[600]} size={20} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-center gap-2 text-center">
                    <IoImage color={colors.purple[600]} size={20} />
                    <p className="font-semibold text-purple-600">
                      Drop an artist cover
                    </p>
                  </div>
                )}

                {isDropCover && (
                  <div
                    onDrop={(e) => handleDropImage(e, 'cover')}
                    onDragLeave={() => setIsDropCover(false)}
                    className="absolute top-0 flex h-full w-full items-center justify-center bg-black/80"
                  >
                    <div className="pointer-events-none">
                      <p className="text-white font-bold text-xl">
                        Drop your file
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between gap-3 md:flex-col md:items-stretch">
            <p className="font-bold">Social Links</p>
            <button
              type="button"
              className="flex items-center justify-center gap-2 rounded-lg border border-purple-600 px-3 py-2 text-sm font-semibold text-purple-600 hover:bg-purple-50 md:w-full"
              onClick={handleAddSocialLink}
            >
              <IoAdd size={18} />
              Add link
            </button>
          </div>

          {socialLinks.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">
              No social links added yet.
            </p>
          ) : (
            <div className="mt-2">
              <div className="grid grid-cols-[minmax(0,220px),minmax(0,1fr),42px] gap-3 px-1 text-xs font-bold uppercase tracking-wide text-gray-500 base:grid-cols-[minmax(0,180px),minmax(0,1fr),42px] md:hidden">
                <span>Platform</span>
                <span>URL</span>
                <span />
              </div>

              <div className="mt-2 space-y-2">
                {socialLinks.map((link, index) => (
                  <div
                    key={link.id ?? `social-link-${index}`}
                    className="grid grid-cols-[minmax(0,220px),minmax(0,1fr),42px] gap-3 base:grid-cols-[minmax(0,180px),minmax(0,1fr),42px] md:grid-cols-1 md:rounded-xl md:border md:border-gray-200 md:p-3"
                  >
                    <div className="min-w-0">
                      <p className="mb-1 hidden text-xs font-bold uppercase tracking-wide text-gray-500 md:block">
                        Platform
                      </p>
                      <select
                        value={link.socialMediaId}
                        onChange={(e) =>
                          handleSocialLinkChange(
                            index,
                            'socialMediaId',
                            e.currentTarget.value,
                          )
                        }
                        className="block w-full appearance-none rounded border border-gray-300 bg-white px-3 py-2 pr-8 text-sm leading-tight cursor-pointer focus:border-gray-500 focus:bg-white focus:outline-none"
                      >
                        {(socialMediaOptions ?? []).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="min-w-0">
                      <p className="mb-1 hidden text-xs font-bold uppercase tracking-wide text-gray-500 md:block">
                        URL
                      </p>
                      <input
                        value={link.url}
                        onChange={(e) =>
                          handleSocialLinkChange(
                            index,
                            'url',
                            e.currentTarget.value,
                          )
                        }
                        placeholder="https://..."
                        className={`${textInputClassName} py-2 text-sm`}
                      />
                    </div>

                    <button
                      type="button"
                      className="flex items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50 md:h-10 md:w-full"
                      onClick={() => handleRemoveSocialLink(index)}
                      title="Remove social link"
                    >
                      <IoTrash size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {currentArtist?.id && (
          <div className="mt-4">
            <p className="font-bold">Catalog Governance</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <CatalogStatusBadge label="Discover" value={discoveryStatus} />
              <CatalogStatusBadge label="Claim" value={profileClaimStatus} />
              <CatalogStatusBadge label="Photo" value={visualAssetStatus} />
              <CatalogStatusBadge label="Cover" value={coverAssetStatus} />
            </div>

            <div className="mt-3 grid grid-cols-3 gap-3 base:grid-cols-1">
              <div className="rounded-xl border border-gray-200 p-3">
                <p className="text-sm font-bold">Profile Claim Status</p>
                <div className="mt-2 flex items-center gap-2 base:flex-col base:items-stretch">
                  <select
                    value={profileClaimStatus}
                    onChange={(e) =>
                      setProfileClaimStatus(
                        e.currentTarget.value as ProfileClaimStatus,
                      )
                    }
                    className="block appearance-none flex-1 bg-white border border-gray-300 rounded px-3 py-2 pr-8 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 cursor-pointer text-sm"
                  >
                    {profileClaimStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    title="Update"
                    className="w-auto min-w-[92px] text-xs base:w-full"
                    variant="outline"
                    type="button"
                    isLoading={governanceActionLoading === 'profileClaim'}
                    onClick={handleUpdateProfileClaimStatus}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-3">
                <p className="text-sm font-bold">Photo Asset Status</p>
                <div className="mt-2 flex items-center gap-2 base:flex-col base:items-stretch">
                  <select
                    value={visualAssetStatus}
                    onChange={(e) =>
                      setVisualAssetStatus(
                        e.currentTarget.value as VisualAssetStatus,
                      )
                    }
                    className="block appearance-none flex-1 bg-white border border-gray-300 rounded px-3 py-2 pr-8 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 cursor-pointer text-sm"
                  >
                    {visualAssetStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    title="Update"
                    className="w-auto min-w-[92px] text-xs base:w-full"
                    variant="outline"
                    type="button"
                    isLoading={governanceActionLoading === 'visualAsset'}
                    onClick={handleUpdateVisualAssetStatus}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-3">
                <p className="text-sm font-bold">Cover Asset Status</p>
                <div className="mt-2 flex items-center gap-2 base:flex-col base:items-stretch">
                  <select
                    value={coverAssetStatus}
                    onChange={(e) =>
                      setCoverAssetStatus(
                        e.currentTarget.value as CoverAssetStatus,
                      )
                    }
                    className="block appearance-none flex-1 bg-white border border-gray-300 rounded px-3 py-2 pr-8 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 cursor-pointer text-sm"
                  >
                    {coverAssetStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    title="Update"
                    className="w-auto min-w-[92px] text-xs base:w-full"
                    variant="outline"
                    type="button"
                    isLoading={governanceActionLoading === 'coverAsset'}
                    onClick={handleUpdateCoverAssetStatus}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="h-[1px] bg-gray-300/50 my-5" />

        <div className="ml-auto flex w-full justify-end gap-2 md:flex-col">
          {currentArtist?.id && (
            <Button
              title={discoveryStatus === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
              variant="outline"
              className="w-auto min-w-[132px] md:w-full"
              isLoading={isPulisherLoading}
              onClick={() => handlePublisherArtist(currentArtist.id)}
              type="button"
            />
          )}

          <Button
            title="Save"
            variant="purple"
            className="w-auto min-w-[132px] md:w-full"
            isLoading={isLoading}
            type="submit"
          />
        </div>
      </form>
    </div>
  )
}
